// markdownItMathPipe.js
// Prevents | inside $...$ and $$...$$ math spans from being interpreted as
// table cell separators by the markdown-it table block rule.
//
// Strategy:
//   1. Wrap the built-in 'table' block rule. Before calling the original
//      rule, replace every | that sits inside a math span with a private-use
//      placeholder character (\uE000) in state.src. Because the replacement
//      is 1-for-1, all position indices (bMarks, eMarks, tShift…) stay valid.
//   2. Call the original rule. The table parser now sees no stray pipes inside
//      math expressions and creates the correct cell tokens.
//   3. Restore state.src immediately afterwards.
//   4. Add a core rule that runs just before 'inline' processing and replaces
//      every remaining placeholder in inline token content back to |.

const PLACEHOLDER = '\uE000'; // Unicode Private Use Area – never in normal text

/**
 * Collect the absolute src positions of every | character that lies inside a
 * $...$ or $$...$$ span on lines [startLine, endLine).
 */
function mathPipePositions(state, startLine, endLine) {
  const src = state.src;
  const positions = [];

  for (let line = startLine; line < endLine; line++) {
    if (state.isEmpty(line)) continue;
    const lineStart = state.bMarks[line] + state.tShift[line];
    const lineEnd = state.eMarks[line];

    let i = lineStart;
    while (i < lineEnd) {
      const ch = src[i];
      if (ch !== '$') { i++; continue; }

      const doubleDollar = src[i + 1] === '$';
      const delimLen = doubleDollar ? 2 : 1;
      const mathStart = i + delimLen;

      // A space/tab/newline right after the opening $ means it's not math
      if (!doubleDollar && mathStart < lineEnd) {
        const next = src.charCodeAt(mathStart);
        if (next === 0x20 || next === 0x09 || next === 0x0a) { i++; continue; }
      }

      const delim = doubleDollar ? '$$' : '$';
      const closeIdx = src.indexOf(delim, mathStart);
      if (closeIdx === -1 || closeIdx >= lineEnd) { i++; continue; }

      // Collect | positions inside the math span
      for (let j = mathStart; j < closeIdx; j++) {
        if (src[j] === '|') positions.push(j);
      }

      i = closeIdx + delimLen;
    }
  }

  return positions;
}

/**
 * Return a new string with the characters at the given (sorted) positions
 * replaced by PLACEHOLDER.
 */
function applyReplacements(src, positions) {
  if (!positions.length) return src;
  let result = '';
  let last = 0;
  for (const pos of positions) {
    result += src.slice(last, pos) + PLACEHOLDER;
    last = pos + 1;
  }
  return result + src.slice(last);
}

export default (md) => {
  // ── Step 1: wrap the table block rule ───────────────────────────────────
  const tableRuleIdx = md.block.ruler.__find__('table');
  if (tableRuleIdx !== -1) {
    const entry = md.block.ruler.__rules__[tableRuleIdx];
    const originalFn = entry.fn;
    const originalAlt = entry.alt;

    md.block.ruler.at('table', (state, startLine, endLine, silent) => {
      const positions = mathPipePositions(state, startLine, endLine);
      const originalSrc = state.src;

      if (positions.length) {
        state.src = applyReplacements(originalSrc, positions);
      }

      const result = originalFn(state, startLine, endLine, silent);

      if (positions.length) {
        state.src = originalSrc;
      }

      return result;
    }, { alt: originalAlt });
  }

  // ── Step 2: core rule – restore placeholder before inline processing ────
  md.core.ruler.before('inline', 'math_pipe_restore', (state) => {
    for (const token of state.tokens) {
      if (token.type === 'inline' && token.content.includes(PLACEHOLDER)) {
        token.content = token.content.split(PLACEHOLDER).join('|');
      }
    }
  });
};



// WEBPACK FOOTER //
// ./src/extensions/libs/markdownItMathPipe.js